import { Component, OnInit, Inject, Signal, computed } from '@angular/core';
import { Router, GuardsCheckEnd } from '@angular/router';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WINDOW } from 'src/app/core/core.module';
import { Identity, IdentityService } from 'src/app/core/identity.service';
import { AuthService } from 'src/app/core/auth.service';
import { MenuService } from 'src/app/core/menu.service';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { RecruitingActions } from 'src/app/root-store/recruiting-store/actions';
import * as InAppNotificationsActions from 'src/app/in-app-notifications/store/actions/in-app-notifications.actions';
import * as HubActions from 'src/app/root-store/hub-connection-store/hub-connection.actions';
import { NovaMatIconRegistryService } from 'src/app/shared/services/mat-icon-registry.service';
import { DelegationQuickAccessDialogComponent } from 'src/app/delegations/delegation-quick-access-dialog/delegation-quick-access-dialog.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { from, Observable, of } from 'rxjs';
import { AsyncPipe, CommonModule } from '@angular/common';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { IfLDFeatureDirective } from 'src/app/shared/directives/if-ld-feature.directive';
import { UserProfile } from 'src/app/core/models/user-profile.model';
import { toSignal } from '@angular/core/rxjs-interop';

enum TOP_NAV_MENU_MODULES {
    CORPORATE = 'corporateDirectory',
    NOVA_IDEAS = 'novaIdeasPortal',
    HELP_DIRECTORY = 'help_directory',
    USER_DELEGATES = 'userDelegates'
}

@UntilDestroy()
@Component({
    selector: 'nova-top-nav-menu',
    standalone: true,
    imports: [
        MatDialogModule,
        MatToolbarModule,
        MatButtonModule,
        MatMenuModule,
        MatDividerModule,
        MatIconModule,
        AsyncPipe,
        CommonModule,
        IfLDFeatureDirective
    ],
    templateUrl: './top-nav-menu.component.html',
    styleUrls: ['./top-nav-menu.component.scss']
})
export class TopNavMenuComponent implements OnInit {
    identity: Identity;
    userId = 0;
    aspNetUserId: string;

    isMenuInitialized = false;
    hideTopNavMenu = true;
    isUserDelegationsDialogActive = false;

    auth0FixNovaImpersonationSegmentsSignal: Signal<boolean>;
    auth0NovaLinkedProfilesSignal: Signal<boolean>;
    auth0NovaImpersonationSignal: Signal<boolean>;
    profilesSignal: Signal<UserProfile[]>;
    profilesExistSignal: Signal<boolean>;
    impersonationSignal: Signal<boolean>;

    readonly openedModuleSessionStorageKey = 'openedModule';
    readonly modules = TOP_NAV_MENU_MODULES;

    constructor(
        private readonly _router: Router,
        private readonly _identityService: IdentityService,
        private readonly _authService: AuthService,
        private readonly _menuService: MenuService,
        private readonly _store: Store,
        private readonly _novaMatIconRegistry: NovaMatIconRegistryService,
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _dialog: MatDialog,
        private readonly _ldFeatureManager: LDFeatureManager
    ) {
        this._novaMatIconRegistry.addSvgIcon('delegate', 'assets/images/svg/account-arrow-right.svg');

        this.auth0FixNovaImpersonationSegmentsSignal = this._ldFeatureManager.isEnabledSignal(
            FeatureFlag.Auth0FixNovaImpersonationSegments
        );
        //This is a temporary logic for FF Segments (EPD-3080) to be removed togater with the feature flags
        const refreshUser$ = this.auth0FixNovaImpersonationSegmentsSignal()
            ? this._identityService.identity$.pipe(
                  filter((identity) => !!identity?.userId),
                  take(1),
                  switchMap((identity: Identity) => {
                      const ldUser = this._ldFeatureManager.getUser();
                      if (ldUser?.key !== identity?.userId) {
                          return from(this._ldFeatureManager.changeUser(identity));
                      } else {
                          return of({});
                      }
                  })
              )
            : of({});

        this.auth0NovaLinkedProfilesSignal = this._ldFeatureManager.isEnabledSignal(
            FeatureFlag.Auth0NovaLinkedProfiles
        );
        this.auth0NovaImpersonationSignal = this._ldFeatureManager.isEnabledSignal(FeatureFlag.Auth0NovaImpersonation);
        this.profilesSignal = toSignal(
            computed(() =>
                refreshUser$.pipe(
                    switchMap(() =>
                        this.auth0NovaLinkedProfilesSignal()
                            ? this._identityService.isAuthenticated$.pipe(
                                  switchMap((auth) => (auth ? this._authService.getProfiles() : of([])))
                              )
                            : of([])
                    )
                )
            )(),
            { initialValue: [] }
        );
        this.profilesExistSignal = computed(() => this.profilesSignal()?.length > 1);
        this.impersonationSignal = toSignal(
            computed(() =>
                refreshUser$.pipe(
                    switchMap(() =>
                        this.auth0NovaImpersonationSignal()
                            ? this._identityService.user$.pipe(map((response) => response?.impersonation))
                            : of(false)
                    )
                )
            )(),
            { initialValue: false }
        );
    }

    get isLoggedIn(): boolean {
        return this.userId > 0;
    }

    get userInitials(): string {
        return `${this.identity?.firstName?.charAt(0).toUpperCase()}${this.identity?.lastName
            ?.charAt(0)
            .toUpperCase()}`;
    }

    get userDisplayName(): string {
        return `${this.identity?.firstName}`;
    }

    ngOnInit(): void {
        this._router.events
            .pipe(
                untilDestroyed(this),
                filter((event) => event instanceof GuardsCheckEnd),
                tap((event: GuardsCheckEnd) => {
                    this.hideTopNavMenu = this._menuService.isHiddenMenuRoute(event.urlAfterRedirects);

                    this.userId = 0;
                    this.identity = this._identityService.current;
                    if (this.identity && !MenuService.publicRoutes.includes(event.url)) {
                        this.userId = this.identity.contactId;
                        if (!this.isMenuInitialized) {
                            this._store.dispatch(
                                RecruitingActions.DealsActions.getCandidateOffersRecruiterReviewCount({
                                    recruiterId: this.userId
                                })
                            );
                        }

                        this.aspNetUserId = this.identity.userId;
                        if (this.aspNetUserId && !this.isMenuInitialized) {
                            this._store.dispatch(InAppNotificationsActions.getUnreadInAppNotificationsCount());
                            this.isMenuInitialized = true;
                        }
                    }
                })
            )
            .subscribe();
    }

    signOut(): void {
        this._store.dispatch(RecruitingActions.DealsActions.clearRecruiterDealAlertData());
        this._store.dispatch(InAppNotificationsActions.clearInAppNotificationData());
        this._store.dispatch(HubActions.disconnectFromCoreHub());
        this.isMenuInitialized = false;
        if (this.auth0NovaImpersonationSignal()) {
            const profiles = this.profilesSignal();
            this._resolveImpersonation(profiles).subscribe(() => this._identityService.logout());
        } else {
            this._identityService.logout();
        }
    }

    openModule(moduleName: string, path: string): void {
        sessionStorage.setItem(this.openedModuleSessionStorageKey, moduleName);
        switch (moduleName) {
            case this.modules.CORPORATE:
            case this.modules.USER_DELEGATES:
                this._router.navigate([path]);
                break;
            case this.modules.NOVA_IDEAS:
            case this.modules.HELP_DIRECTORY:
                this._window.open(path);
                break;
        }
    }

    changeProfile(userId: string) {
        if (this.auth0NovaLinkedProfilesSignal()) {
            this._authService.changeProfile(userId);
        }
    }

    openUserDelegationsDialog() {
        this.isUserDelegationsDialogActive = true;
        const dialogRef = this._dialog.open(DelegationQuickAccessDialogComponent, {
            panelClass: ['nova__dialog']
        });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.isUserDelegationsDialogActive = false));
    }

    private _resolveImpersonation(profiles: UserProfile[]): Observable<unknown> {
        if (this.auth0NovaImpersonationSignal() && profiles?.some((p) => p.isImpersonated && p.isDefaultProfile)) {
            const mainProfileUserId = profiles.find((p) => p.isMain)?.userId;
            if (mainProfileUserId) {
                return this._authService.stopImpersonation(mainProfileUserId);
            }
        }
        return of({});
    }
}
